/**
 * Desk number helpers — FinTech UX scan rules (mono/tabular consumers).
 * Never invent live balances or costume missing data as zero.
 */
import { formatCurrency, formatNumber } from "@/shared/utils/formatters";

/** Stable sign slot so P&L width does not jump positive ↔ negative. */
export function formatSignedCurrency(value: number): string {
  const abs = formatCurrency(Math.abs(value));
  if (value > 0) return `+${abs}`;
  if (value < 0) return `−${abs}`;
  return `\u00A0${abs}`;
}

export function formatSignedPercent(value: number): string {
  const abs = Math.abs(value).toFixed(2);
  if (value > 0) return `+${abs}%`;
  if (value < 0) return `−${abs}%`;
  return `\u00A0${abs}%`;
}

export function formatQty(q: number | null | undefined): string {
  if (q == null || Number.isNaN(q)) return "—";
  const abs = Math.abs(q);
  const decimals = abs > 0 && abs < 1 ? 4 : abs % 1 !== 0 ? 4 : 0;
  return formatNumber(q, { decimals });
}

export type PositionSide = "long" | "short" | "flat";

export function resolvePositionSide(
  row: Record<string, unknown>
): PositionSide {
  const raw = String(row.side ?? row.position_side ?? row.positionSide ?? "")
    .toLowerCase()
    .trim();
  if (raw === "buy" || raw === "long") return "long";
  if (raw === "sell" || raw === "short") return "short";
  const q = Number(row.quantity);
  if (Number.isFinite(q) && q < 0) return "short";
  if (Number.isFinite(q) && q > 0) return "long";
  return "flat";
}

/** Prefer API status; never costume every row as Open. */
export function resolvePositionStatus(row: Record<string, unknown>): string {
  const s = row.status ?? row.state ?? row.position_status ?? row.positionStatus;
  if (s != null && String(s).trim() !== "") {
    return String(s).toUpperCase();
  }
  const q = Number(row.quantity);
  if (!Number.isFinite(q)) return "—";
  if (q === 0) return "CLOSED";
  return "OPEN";
}

export function finiteDeskNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Last, bid, and ask. Zero is missing, not a print. */
export function finiteTapePrint(v: unknown): number | null {
  const n = finiteDeskNumber(v);
  if (n == null || n <= 0) return null;
  return n;
}

/**
 * Last-print digits follow the size of the print.
 * Two-decimal money format turns sub-cent names into 0.00.
 */
export function formatTapePrice(value: unknown): string {
  const n = finiteTapePrint(value);
  if (n == null) return "—";
  const abs = Math.abs(n);
  let maximumFractionDigits = 2;
  if (abs > 0 && abs < 0.0001) maximumFractionDigits = 8;
  else if (abs > 0 && abs < 0.01) maximumFractionDigits = 6;
  else if (abs > 0 && abs < 1) maximumFractionDigits = 4;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(n);
}

export function positionMarkPrice(row: Record<string, unknown>): number | null {
  return finiteDeskNumber(
    row.current_price ??
    row.currentPrice ??
    row.mark_price ??
    row.markPrice ??
    row.last_price ??
    row.lastPrice
  );
}

export function positionAvgPrice(row: Record<string, unknown>): number | null {
  return finiteDeskNumber(
    row.average_price ?? row.averagePrice ?? row.avgPrice ?? row.avg
  );
}

export function positionMarketValue(
  row: Record<string, unknown>
): number | null {
  return finiteDeskNumber(row.market_value ?? row.marketValue);
}

export function positionUnrealizedPnl(
  row: Record<string, unknown>
): number | null {
  return finiteDeskNumber(row.unrealized_pnl ?? row.unrealizedPnl);
}

export function tradeDeskHref(symbol: string, intent?: "close"): string {
  const params = new URLSearchParams();
  if (symbol) params.set("symbol", symbol);
  if (intent === "close") params.set("intent", "close");
  const q = params.toString();
  return q ? `/trading/execution?${q}` : "/trading/execution";
}

/** Default stale window when realtime is off. */
export const STALE_MS = 60_000;
